import { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import logo from "../../assets/logo-black.png";
import dashboardIcon from "../../assets/dashboard-icon.png";
import userIcon from "../../assets/user-icon.png";
import senderIcon from "../../assets/sender-icon.png";
import receiverIcon from "../../assets/receiver-icon.png";
import customsIcon from "../../assets/agent-icon.png";
import declarationIcon from "../../assets/declaration-icon.png";
import collapseIcon from "../../assets/collapse-icon.png";
import paymentIcon from "../../assets/history-icon.png";


import './Sidebar.css';
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowBackToDashboardButton } from '../../store/reducers'; 

import { gsap } from 'gsap';
import { ChevronRight } from 'lucide-react';
import { useLanguage, getSidebarTranslation } from '../../hooks/useLanguage';

const Sidebar = ({isDarkMode, setIsDarkMode, isSidebarCollapsed, setIsSidebarCollapsed, isScrolledPastWorkflowTabs, isScrolledPastPageHeader, navBreadcrumb, activeWorkflowName: activeWorkflowNameProp}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isSpanish] = useLanguage();
  const headerTitleRef = useRef(null);

  // Get active workflow session name (use prop from Main when on dashboard for breadcrumb, else store)
  const activeWorkflowSession = useSelector((state) => {
    const workflowSessions = state.workflowSessions;
    const activeId = workflowSessions?.activeSessionId;
    return activeId ? workflowSessions?.sessions?.[activeId] : null;
  });
  const activeWorkflowName = activeWorkflowNameProp ?? activeWorkflowSession?.name ?? 'Workflow Name';

  const [navLinks] = useState([
    {
      name: 'home',
      path: '/',
      icon: dashboardIcon
    },
    {
      name: 'userManagement',
      navName: 'User Management',
      path: '/user-management',
      icon: userIcon
    },
    {
      name: 'senderManagement',
      navName: 'Sender Management',
      path: '/sender-management',
      icon: senderIcon
    },
    {
      name: 'receiversManagement',
      navName: 'Receivers Management',
      path: '/receiver-management',
      icon: receiverIcon
    },
    {
      name: 'customsAgent',
      navName: 'Customs Agent',
      path: '/customs-agent',
      icon: customsIcon
    },
    {
      name: 'declarationManagement',
      navName: 'Declaration Management',
      path: '/declaration-management',
      icon: declarationIcon
    },
    {
      name: 'creditHistory',
      navName: 'Credit History',
      path: '/credit-history',
      icon: paymentIcon
    },
  ]);

  const isActive = (path) => location.pathname === path;

  const handleNavLinkClick = () => {
    dispatch(setShowBackToDashboardButton(false));
  };

  // Function to get display name
  const getDisplayName = () => {
    if (location.pathname === '/') {
      return activeWorkflowName;
    }
    return navLinks.find(link => link.path === location.pathname)?.navName || 'Workflow Name';
  };

  // Animation
  useEffect(() => {
    const sections = document.querySelectorAll('.fadeUp');
    const tl = gsap.timeline();

    sections.forEach(section => {
      tl.to(section,
        {
          stagger: 0.15,
          duration: 0.15,
          opacity: 1,
          y: 0,
          ease: "back",
        }
      );
    });
  }, [location]);

  // Opacity animation for header title based on scroll state
  useEffect(() => {
    if (!headerTitleRef.current) return;

    const shouldShow = 
      (location.pathname === '/' && isScrolledPastWorkflowTabs) ||
      (location.pathname !== '/' && isScrolledPastPageHeader);

    gsap.to(headerTitleRef.current, {
      opacity: shouldShow ? 1 : 0,
      duration: 0.3,
      ease: "power2.out"
    });
  }, [location.pathname, isScrolledPastWorkflowTabs, isScrolledPastPageHeader]);

 
  return (
    <div className={`sidebar ${isDarkMode ? 'dark-mode' : ''} ${isSidebarCollapsed && "sidebar-collapsed"}`}>
      <div className={`hidden w-10 h-8 nav-header-containera absolute top-[5.5rem] right-[-2.5rem] ${(location.pathname === "/" ? !isScrolledPastWorkflowTabs : !isScrolledPastPageHeader) && "bg-white-force"}`}  style={{ borderBottomRightRadius: '3rem'}}>
                <div className="size-full bg-white" style={{borderTopLeftRadius: '2rem', borderBottomRightRadius: '3rem'}}></div>
              </div>
              <div className={`pointer-events-none absolute top-[1.75rem] w-full ${isSidebarCollapsed ? "right-[-8rem]" : "right-[-18.5rem]"} nav-header-title`} id="nav-header-title">
                <h2 ref={headerTitleRef} className="text-left w-max flex items-end gap-1.5 flex-wrap" style={{ opacity: 0 }}>
                  {location.pathname === '/' && navBreadcrumb ? (
                    navBreadcrumb.split(' > ').map((segment, i, arr) => (
                      <React.Fragment key={i}>
                        <span className={i === 0 ? 'text-2xl font-bold' : 'text-base font-normal'}>{segment}</span>
                        {i < arr.length - 1 && <ChevronRight className="size-4 flex-shrink-0 text-gray-500 mb-[0.15rem]" aria-hidden />}
                      </React.Fragment>
                    ))
                  ) : (
                    <span className="text-2xl font-bold">{getDisplayName()}</span>
                  )}
                </h2>
                </div>
    <div className={`${isSidebarCollapsed && "sidebar-collapsed-2"}`}>
    <div className="sidebar-content">
      <div className='flex justify-between gap-2 items-center mb-10 sidebar-top px-3'>
      <div className="logo fadeUp"
      onClick={() => (navigate('/'))}>
        <img 
          src={logo} 
          alt="logo"
          className="logo-icon h-10"
        />
      </div>
      <div className="fadeUp  p-1.5 rounded-full cursor-pointer collapse-container"
      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
        <img 
          src={collapseIcon} 
          alt="logo"
          className="logo-icon h-7"
        />
      </div>
      </div>

      <nav className='flex-1'>
        <ul className="space-y-2">
          {navLinks.map((link) => (
            <li key={link.path} className='fadeUp' >
              <Link
                to={link.path}
                onClick={handleNavLinkClick}
                className={`session-item flex items-center px-3 py-2 rounded-lg transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'active text-dark text-primary'
                    : ' '
                }`}
              >
                <img 
          src={link.icon} 
          alt="logo"
          className={`session-icon`}
        />
                <span className={`nav-text ${isActive(link.path) && ' font-bold'}`}>{getSidebarTranslation(link.name, isSpanish) || link.name.charAt(0).toUpperCase() + link.name.slice(1)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      </div>
      </div>
    </div>
  );
};

export default Sidebar;